import React, { useState, useContext, useEffect, useRef } from "react";
import { Link, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import UserContext from "../context/UserContext";
import axiosClient from "../config/axiosClient";
import API_ENDPOINTS from "../config/api"; 
import "../App.css";

const Layout = () => {
  const { user, setUser } = useContext(UserContext);
  const [navMenus, setNavMenus] = useState([]);
  const [openMenus, setOpenMenus] = useState({});
  const [openMenu, setOpenMenu] = useState(null);
  
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  
  const [notifications, setNotifications] = useState([]);
  const timerRef = useRef(null); 
  const navigate = useNavigate();
  const location = useLocation();
  const storedUser = localStorage.getItem("user");

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const res = await axiosClient.get(API_ENDPOINTS.myMenus);
        setNavMenus(res.data || []);
      } catch (err) { console.error("❌ Failed to fetch dynamic menus:", err); }
    };
    if (user) fetchMenus();
  }, [user]);

  const fetchAllNotifications = async () => {
    const currentId = user?.id || (storedUser ? JSON.parse(storedUser).id : null);
    if (!currentId) return;
    try {
      const [leaveRes, otRes, paymentRes] = await Promise.all([
        axiosClient.get(API_ENDPOINTS.leaveNotifications(currentId)),
        axiosClient.get(API_ENDPOINTS.otNotifications(currentId)),
        axiosClient.get(API_ENDPOINTS.paymentNotifications(currentId)).catch(() => ({ data: [] }))
      ]);
      setNotifications([...leaveRes.data, ...otRes.data, ...paymentRes.data]);
    } catch (error) { console.error("🔔 Fetch notifications failed", error); }
  };

  useEffect(() => {
    fetchAllNotifications();
    const interval = setInterval(fetchAllNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleNotificationClick = async (n) => {
    try {
      const msg = n.message.toLowerCase();
      if (msg.includes("ot")) await axiosClient.put(API_ENDPOINTS.markOtAsRead(n.id));
      else if (msg.includes("payment")) await axiosClient.put(API_ENDPOINTS.markPaymentAsRead(n.id));
      else await axiosClient.put(API_ENDPOINTS.markLeaveAsRead(n.id));
      fetchAllNotifications();
      setIsMobileOpen(false);
      navigate(n.link || "/dashboard");
    } catch (err) { console.error(err); }
  };

  const toggleMenu = (id) => setOpenMenus(prev => ({ ...prev, [id]: !prev[id] }));
  const handleLogout = () => { localStorage.clear(); setUser(null); navigate("/login"); };

  if (!user && !storedUser) return <Navigate to="/login" replace />;

  return (
    <div id="wrapper" style={{ display: "flex", height: "100vh" }}>
      <button 
        className="floating-menu-btn" 
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label="Toggle Menu"
      >
        <i className={`fas ${isMobileOpen ? 'fa-times' : 'fa-bars'}`}></i>
      </button>

      {isMobileOpen && <div className="sidebar-overlay" onClick={() => setIsMobileOpen(false)}></div>}

      <ul className={`navbar-nav bg-gradient-primary sidebar sidebar-dark accordion ${isMobileOpen ? "mobile-open" : ""}`} id="accordionSidebar">
        {/* Phần Brand cố định */}
        <div className="sidebar-brand-container">
            <div className="sidebar-brand d-flex align-items-center justify-content-center py-3">
              <div className="sidebar-brand-icon"><i className="fas fa-rocket"></i></div>
              <div className="sidebar-brand-text mx-3">PEX-HRM  </div>
            </div>
            <hr className="sidebar-divider my-0" />
        </div>

        {/* Khu vực menu có thể cuộn */}
        <div className="sidebar-scroll-area">
          {navMenus.map((menu) => {
            const hasChildren = menu.children && menu.children.length > 0;
            const isExpanded = openMenus[menu.id];
            return (
              <React.Fragment key={menu.id}>
                <li className={`nav-item ${location.pathname === menu.url ? "active" : ""}`}>
                  <div className="nav-link custom-nav-link d-flex align-items-center" onClick={() => hasChildren ? toggleMenu(menu.id) : (navigate(menu.url || "#"), setIsMobileOpen(false))}>
                    <i className={`fas fa-fw ${menu.icon || 'fa-folder'}`}></i>
                    <span className="ml-3 menu-text">{menu.title}</span>
                    {hasChildren && <i className={`fas fa-chevron-right ml-auto transition-icon ${isExpanded ? 'rotate-90' : ''}`}></i>}
                  </div>
                </li>
                {hasChildren && (
                  <div className={`collapse-inner ${isExpanded ? 'show' : ''}`} style={{ display: isExpanded ? 'block' : 'none' }}>
                    {menu.children.map((child) => (
                      <Link key={child.id} className={`collapse-item nav-link ${location.pathname === child.url ? "active-child" : ""}`} to={child.url} onClick={() => setIsMobileOpen(false)}>
                        <i className={`fas fa-sm mr-2 ${child.icon || 'fa-angle-right'}`}></i>{child.title}
                      </Link>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </ul>

      <div id="content-wrapper" className="d-flex flex-column" style={{ flex: 1, overflowY: "auto" }}>
        <nav className="navbar navbar-expand navbar-light bg-white topbar mb-4 static-top shadow-sm">
          <ul className="navbar-nav ml-auto">
            <li className="nav-item dropdown no-arrow mx-2" onMouseEnter={() => setOpenMenu("bell")} onMouseLeave={() => timerRef.current = setTimeout(() => setOpenMenu(null), 300)}>
              <a className="nav-link dropdown-toggle" href="#"><i className="fas fa-bell text-primary"></i>
                {notifications.length > 0 && <span className="badge badge-danger badge-counter pulse-animation">{notifications.length}</span>}
              </a>
              {openMenu === "bell" && (
                <div className="dropdown-list dropdown-menu dropdown-menu-right shadow animated--grow-in show">
                  <h6 className="dropdown-header">Notifications Center</h6>
                  {notifications.map((n, i) => (
                    <div key={i} className="dropdown-item" onClick={() => handleNotificationClick(n)} style={{cursor: 'pointer'}}>
                      <span className="font-weight-bold">{n.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </li>
            <li className="nav-item dropdown no-arrow" onMouseEnter={() => setOpenMenu("avatar")} onMouseLeave={() => timerRef.current = setTimeout(() => setOpenMenu(null), 300)}>
              <a className="nav-link dropdown-toggle" href="#"><span className="mr-2 d-none d-lg-inline text-gray-600 font-weight-bold">{user?.userName}</span>
                <img className="img-profile rounded-circle border border-primary" src={`https://ui-avatars.com/api/?name=${user?.userName}&background=4e73df&color=fff`} alt="avatar" />
              </a>
              {openMenu === "avatar" && (
                <div className="dropdown-menu dropdown-menu-right shadow animated--grow-in show">
                  <Link className="dropdown-item" to="/profile"><i className="fas fa-user fa-sm mr-2 text-gray-400"></i>Profile</Link>
                  <button className="dropdown-item text-danger" onClick={handleLogout}><i className="fas fa-sign-out-alt fa-sm mr-2 text-gray-400"></i>Logout</button>
                </div>
              )}
            </li>
          </ul>
        </nav>
        <div className="container-fluid px-4"><Outlet /></div>  
      </div>

      <style>{`
        .sidebar { background: linear-gradient(180deg, #4e73df 10%, #224abe 100%); transition: left 0.15s ease-out; display: flex; flex-direction: column; height: 100vh; }
        .sidebar-brand-container { flex-shrink: 0; }
        .sidebar-scroll-area { flex: 1; overflow-y: auto; overflow-x: hidden; width: 100%; }
        .sidebar-scroll-area::-webkit-scrollbar { width: 6px; }
        .sidebar-scroll-area::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.3); border-radius: 10px; }
        
        .custom-nav-link { padding: 12px 20px !important; margin: 4px 10px; border-radius: 10px; color: rgba(255,255,255,0.85) !important; font-weight: 500; cursor: pointer; transition: 0.3s; }
        .custom-nav-link:hover { background: rgba(255,255,255,0.2); color: #fff !important; }
        .collapse-inner { background: rgba(0,0,0,0.1) !important; margin: 0 10px; border-radius: 10px; }
        .collapse-item { padding: 10px 25px !important; color: rgba(255,255,255,0.7) !important; }
        .collapse-item:hover { color: #fff !important; }
        .active-child { color: #fff !important; font-weight: bold; background: rgba(255,255,255,0.1); }
        .floating-menu-btn { position: fixed; bottom: 30px; right: 30px; z-index: 9999; width: 50px; height: 50px; background: #4e73df; color: white; border: none; border-radius: 50%; box-shadow: 0 4px 12px rgba(0,0,0,0.3); cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .sidebar-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.4); z-index: 9997; }
        .rotate-90 { transform: rotate(90deg); }
        .transition-icon { transition: transform 0.2s; }
        @media (max-width: 768px) {
          .sidebar { position: fixed; top: 0; left: -250px; z-index: 9998; width: 250px; }
          .sidebar.mobile-open { left: 0; box-shadow: 5px 0 15px rgba(0,0,0,0.3); }
        }
      `}</style>
    </div>
  );
};

export default Layout;